// src/App.js
import React from "react";
import { BrowserRouter as Router, Route, Routes, Link, useNavigate } from "react-router-dom";
import { TrendingUp, BarChart2, Shield, Target, Award, Check, Database, RefreshCw, Lock } from "lucide-react";
import Signup from "./Signup.js";
import PaymentPage from "./PaymentPage.js";
import TermsOfService from "./TermsOfService.jsx";
import PrivacyPolicy from "./PrivacyPolicy.jsx";
import RefundPolicy from "./RefundPolicy.jsx";
import Contact from "./Contact.jsx";
import MembersLogin from "./MemberLogin.js";
import ConfirmEmail from "./ConfirmEmail.js";
import PaymentSuccess from "./PaymentSuccess.jsx";
import StripeSubscribeButton from "./StripeSubscribeButton.jsx";
import ProtectedMembersPage from "./ProtectedMembersPage.jsx";
import Footer from "./Footer.jsx";
import TickerLink from "./TickerLink.jsx";
import FfvLanding from "./FfvLanding.jsx";
import LogoLink from "./LogoLink.jsx";
import { useCrossTabSignOut } from "./crossTabSignOut.js";
import "./App.css";

function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="homepage-container">

      {/* ── Sticky Navigation ── */}
      <nav className="landing-nav">
        <div className="nav-inner">
          <LogoLink className="nav-logo" iconSize={20} />
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#pricing">Pricing</a>
          </div>
          <button className="nav-cta" onClick={() => navigate("/signup")}>
            Get Started
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="hero-section">
        <div className="hero-left">
          <div className="hero-badge">
            <span>S&amp;P 500 &amp; Nasdaq Coverage</span>
          </div>
          <h1>Value Investing,<br />Data-Driven.</h1>
          <p>
            Access forward fair value calculations for hundreds of stocks,
            updated weekly. Know what a stock is worth — before you buy.
          </p>
          <div className="hero-ctas">
            <button onClick={() => navigate("/signup")}>Start Free Trial</button>
            <a href="#how-it-works">See How It Works →</a>
          </div>
          <div className="hero-stats">
            <div>
              <strong>500+</strong>
              <span>Stocks Tracked</span>
            </div>
            <div>
              <strong>Weekly</strong>
              <span>Model Updates</span>
            </div>
            <div>
              <strong>$4.99</strong>
              <span>Per Month</span>
            </div>
          </div>
        </div>

        <div className="hero-right">
          <div className="mock-dashboard">
            <div className="mock-header">
              <span>S&amp;P 500 Valuation Overview</span>
              <span className="mock-preview-label">SAMPLE PREVIEW</span>
            </div>
            <div className="mock-row mock-row--head">
              <span>Ticker</span>
              <span>Fair Value</span>
              <span>Discount</span>
              <span>Risk</span>
            </div>
            <div className="mock-row">
              <span className="mock-ticker"><TickerLink ticker="AAPL" /></span>
              <span>$198.40</span>
              <span className="mock-positive">+12.3%</span>
              <span className="mock-risk mock-risk--low">Low</span>
            </div>
            <div className="mock-row">
              <span className="mock-ticker"><TickerLink ticker="MSFT" /></span>
              <span>$445.20</span>
              <span className="mock-positive">+8.7%</span>
              <span className="mock-risk mock-risk--low">Low</span>
            </div>
            <div className="mock-row">
              <span className="mock-ticker"><TickerLink ticker="NVDA" /></span>
              <span>$892.15</span>
              <span className="mock-negative">-4.2%</span>
              <span className="mock-risk mock-risk--high">High</span>
            </div>
            <div className="mock-row">
              <span className="mock-ticker"><TickerLink ticker="AMZN" /></span>
              <span>$212.80</span>
              <span className="mock-positive">+15.1%</span>
              <span className="mock-risk mock-risk--med">Med</span>
            </div>
            <div className="mock-row">
              <span className="mock-ticker"><TickerLink ticker="JNJ" /></span>
              <span>$174.60</span>
              <span className="mock-positive">+6.8%</span>
              <span className="mock-risk mock-risk--low">Low</span>
            </div>
            <div className="mock-footer">
              <span>+ 495 more stocks</span>
              <span>Illustrative data only</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust Bar ── */}
      <div className="trust-bar">
        <div className="trust-item"><Shield size={15} /><span>SEC-Compliant Disclosures</span></div>
        <div className="trust-divider" />
        <div className="trust-item"><Database size={15} /><span>Fundamental Data Analysis</span></div>
        <div className="trust-divider" />
        <div className="trust-item"><RefreshCw size={15} /><span>Weekly Model Updates</span></div>
        <div className="trust-divider" />
        <div className="trust-item"><Lock size={15} /><span>Secure Member Portal</span></div>
      </div>

      {/* ── Features ── */}
      <section id="features" className="features-section">
        <h2>Everything you need for value investing</h2>
        <p>
          Our proprietary model calculates forward fair value using DCF analysis,
          earnings quality metrics, and momentum signals — all in one place.
        </p>
        <div className="features-grid">
          <div className="feature-card">
            <BarChart2 className="feature-icon" />
            <h3>Forward Fair Value</h3>
            <p>Proprietary DCF-based calculations updated weekly for every stock in our coverage universe.</p>
          </div>
          <div className="feature-card">
            <Target className="feature-icon" />
            <h3>Quality + Value Scoring</h3>
            <p>Composite scores combining earnings quality, balance sheet strength, and relative valuation.</p>
          </div>
          <div className="feature-card">
            <TrendingUp className="feature-icon" />
            <h3>Momentum Signals</h3>
            <p>Technical momentum overlays to help you identify optimal entry and exit timing.</p>
          </div>
          <div className="feature-card">
            <Award className="feature-icon" />
            <h3>AI Curated Picks</h3>
            <p>Weekly top picks synthesized across all models — the highest-conviction opportunities surfaced for you.</p>
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="hiw-section">
        <h2>How it works</h2>
        <div className="hiw-steps">
          <div className="hiw-step">
            <div className="step-num">01</div>
            <h3>Subscribe</h3>
            <p>Create your account and subscribe for $4.99/month. No long-term commitment — cancel anytime.</p>
          </div>
          <div className="hiw-arrow">→</div>
          <div className="hiw-step">
            <div className="step-num">02</div>
            <h3>Browse Valuations</h3>
            <p>Access the full valuation table, sortable by discount to fair value, risk level, quality score, and more.</p>
          </div>
          <div className="hiw-arrow">→</div>
          <div className="hiw-step">
            <div className="step-num">03</div>
            <h3>Invest with Confidence</h3>
            <p>Use our data-driven insights to inform your own investment decisions with a clearer picture of value.</p>
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="pricing-section">
        <h2>Simple, transparent pricing</h2>
        <div className="pricing-card">
          <div className="pricing-badge">Full Access</div>
          <div className="pricing-amount">
            $4.99<span>/month</span>
          </div>
          <p className="pricing-subtitle">Everything included. No hidden fees.</p>
          <ul className="pricing-features">
            <li><Check size={15} />Full S&amp;P 500 &amp; Nasdaq valuation table</li>
            <li><Check size={15} />AI curated weekly picks</li>
            <li><Check size={15} />DCF + Quality + Momentum models</li>
            <li><Check size={15} />Weekly data updates</li>
            <li><Check size={15} />CSV export</li>
            <li><Check size={15} />Cancel anytime</li>
          </ul>
          <button className="pricing-cta" onClick={() => navigate("/signup")}>
            Start Free Trial
          </button>
        </div>
      </section>

      {/* ── Disclaimer ── */}
      <div className="disclaimer-section">
        <p>
          <strong>Important Disclaimer:</strong> TickrTrends is for informational and educational purposes only.
          Nothing on this platform constitutes financial, investment, or legal advice. All valuations are model-based
          estimates and do not guarantee future performance. Past data does not predict future results. Always consult
          a qualified financial advisor before making investment decisions. By using this platform you agree to our{" "}
          <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
          For support, contact <a href="mailto:ryan@tickrtrends.com">ryan@tickrtrends.com</a>.
        </p>
      </div>

    </div>
  );
}

export function App() {
  // Sign Out in one tab signs every open tab out (see crossTabSignOut.js).
  useCrossTabSignOut();

  return (
    <div>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/subscribe" element={<StripeSubscribeButton />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/ffv/:ticker" element={<FfvLanding />} />

        {/* Policy pages */}
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refund" element={<RefundPolicy />} />
        <Route path="/contact" element={<Contact />} />

        {/* Protected routes */}
        <Route path="/members" element={
          <ProtectedMembersPage>
            <MembersLogin />
          </ProtectedMembersPage>
        } />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
      </Routes>

      <Footer />
    </div>
  );
}

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

export default AppWrapper;
